import { getSupabaseBaseUrl } from "@/services/supabaseClient";
import { resolveAddressedProfileName } from "@/pages/shared/profileAddressedName.js";

const FOLA_NAME = "Fola";
const FOLA_AVATAR_URL = "/assets/images/fola.svg";
const FOLA_SUBTITLE = "Clinical Assistant";

function firstText(...values) {
  for (const value of values) {
    const text = String(value || "").trim();
    if (text) return text;
  }
  return "";
}

function normalizeRole(value) {
  return String(value || "").trim().toLowerCase();
}

function isFolaIdentityValue(value) {
  return String(value || "").trim().toLowerCase() === "fola";
}

function isFolaProfile(profile = {}, fallback = "") {
  return [
    profile?.display_name,
    profile?.full_name,
    profile?.name,
    profile?.title,
    fallback,
  ].some(isFolaIdentityValue);
}

function fullNameFromParts(firstName, lastName) {
  return [firstText(firstName), firstText(lastName)].filter(Boolean).join(" ").trim();
}

function resolvePatientMessagingName(patientProfile, fallback = "") {
  const legalFallback = firstText(
    patientProfile?.full_name,
    fullNameFromParts(patientProfile?.first_name, patientProfile?.last_name),
    fallback,
  );
  return resolveAddressedProfileName("patient", patientProfile, legalFallback) || legalFallback;
}

function resolvePublicStorageUrl(bucket, rawPath) {
  const path = String(rawPath || "").trim();
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;

  const base = getSupabaseBaseUrl();
  const cleanPath = path.replace(/^public\//, "").replace(/^\/+/, "");
  if (!base || !cleanPath) return "";

  return `${base}/storage/v1/object/public/${bucket}/${cleanPath}`;
}

function resolveRecordAvatar(record, bucket) {
  if (!record || typeof record !== "object") return "";

  const direct = firstText(
    record?.resolved_avatar_url,
    record?.avatar_url,
    record?.profile_image_url,
    record?.profile_photo_url,
    record?.photo_url,
  );
  if (direct) return direct;

  return resolvePublicStorageUrl(
    bucket,
    firstText(
      record?.profile_photo_path,
      record?.profile_photo_storage_path,
      record?.photo_path,
      record?.avatar_path,
    ),
  );
}

function formatDoctorSurname(name) {
  const cleaned = String(name || "")
    .replace(/^(dr\.?|doctor)\s+/i, "")
    .trim();

  if (!cleaned) return "Doctor";

  const parts = cleaned.split(/\s+/).filter(Boolean);
  const surname = parts[parts.length - 1] || cleaned;
  return `Dr ${surname}`;
}

export function resolveMessagingParticipantName(participant, fallback = "Conversation") {
  const profile =
    participant?.profiles && typeof participant.profiles === "object"
      ? participant.profiles
      : {};
  const doctorProfile =
    profile?.doctor_profile && typeof profile.doctor_profile === "object"
      ? profile.doctor_profile
      : {};
  const publicDoctor =
    profile?.public_doctor && typeof profile.public_doctor === "object"
      ? profile.public_doctor
      : {};
  const patientProfile =
    profile?.patient_profile && typeof profile.patient_profile === "object"
      ? profile.patient_profile
      : {};

  const doctorName = firstText(
    doctorProfile?.full_name,
    publicDoctor?.display_name,
    fullNameFromParts(doctorProfile?.first_name, doctorProfile?.last_name),
  );
  const patientName = resolvePatientMessagingName(patientProfile, profile?.display_name);
  const baseName = firstText(doctorName, patientName, profile?.display_name, fallback);

  if (isFolaProfile(profile, fallback)) {
    return FOLA_NAME;
  }

  if (
    normalizeRole(profile?.role) === "doctor" ||
    doctorName
  ) {
    return formatDoctorSurname(baseName);
  }

  return baseName || fallback;
}

export function resolveMessagingParticipantAvatar(participant) {
  const profile =
    participant?.profiles && typeof participant.profiles === "object"
      ? participant.profiles
      : {};
  const doctorProfile =
    profile?.doctor_profile && typeof profile.doctor_profile === "object"
      ? profile.doctor_profile
      : {};
  const publicDoctor =
    profile?.public_doctor && typeof profile.public_doctor === "object"
      ? profile.public_doctor
      : {};
  const patientProfile =
    profile?.patient_profile && typeof profile.patient_profile === "object"
      ? profile.patient_profile
      : {};

  const role = normalizeRole(profile?.role);

  if (role === "assistant" || isFolaProfile(profile, participant?.title || participant?.fallback)) {
    return FOLA_AVATAR_URL;
  }

  return firstText(
    role === "doctor" ? resolveRecordAvatar(profile, "doctor-profile-images") : "",
    role === "patient" ? resolveRecordAvatar(profile, "patient-profile-images") : "",
    resolveRecordAvatar(publicDoctor, "doctor-profile-images"),
    resolveRecordAvatar(doctorProfile, "doctor-profile-images"),
    resolveRecordAvatar(patientProfile, "patient-profile-images"),
    resolveRecordAvatar(profile, "doctor-profile-images"),
    resolveRecordAvatar(profile, "patient-profile-images"),
    "",
  );
}

export function formatMessagingExplicitTitle(title, { isDoctor = false } = {}) {
  const explicit = String(title || "").trim();
  if (!explicit) return "";
  if (isFolaIdentityValue(explicit)) return FOLA_NAME;
  return isDoctor ? formatDoctorSurname(explicit) : explicit;
}

export function isFolaMessagingIdentity(value) {
  return isFolaIdentityValue(value);
}

export function getFolaMessagingIdentity() {
  return {
    title: FOLA_NAME,
    subtitle: FOLA_SUBTITLE,
    avatarUrl: FOLA_AVATAR_URL,
  };
}
